import React, { useState, useEffect, useMemo, useCallback } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from 'react-i18next'; // 1. Added import

const SESSION_ACCESS_KEY_PREFIX = "dashboard_access_";

export default function DashboardAccessGuard({ dashboardKey, children }) {
    const { t } = useTranslation(); // 2. Initialized hook
    const normalizedKey = useMemo(() => dashboardKey.toLowerCase(), [dashboardKey]);
    const { user } = useAuth();

    const getSessionAccessStatus = useCallback(() => {
        return sessionStorage.getItem(SESSION_ACCESS_KEY_PREFIX + normalizedKey) === 'true';
    }, [normalizedKey]); 
    
    const [isLoading, setIsLoading] = useState(true);
    const [storedCodes, setStoredCodes] = useState({});
    const [enteredCode, setEnteredCode] = useState("");
    const [error, setError] = useState("");
    const [isConfigured, setIsConfigured] = useState(false);
    const [validated, setValidated] = useState(getSessionAccessStatus);
    
    const hasPermanentAccess = user?.role === "admin" || user?.role === "manager";
    
    useEffect(() => {
        setEnteredCode("");
        setError("");
        
        const currentSessionStatus = getSessionAccessStatus();
        setValidated(currentSessionStatus);
        
        if (hasPermanentAccess) {
            sessionStorage.setItem(SESSION_ACCESS_KEY_PREFIX + normalizedKey, 'true'); 
            setValidated(true);
            setIsLoading(false);
            return;
        }

        if (currentSessionStatus) {
             setIsLoading(false);
             return;
        }

        const loadCodes = async () => {
            if (!dashboardKey || !user?.uid) {
                setIsLoading(false);
                return; 
            }
            
            setIsLoading(true);
            setError("");
            
            try {
                if (!db) throw new Error("Firebase DB connection not initialized.");
                
                const docRef = doc(db, "users", user.uid);
                const snap = await getDoc(docRef);

                if (snap.exists()) {
                    const codes = snap.data().dashboardCodes || {}; 
                    const normalized = Object.fromEntries(
                        Object.entries(codes).map(([k, v]) => [k.toLowerCase(), String(v)])
                    );

                    setStoredCodes(normalized);
                    const expectedCode = normalized[normalizedKey];
                    
                    if (expectedCode && expectedCode.trim() !== "") {
                        setIsConfigured(true);
                        setError(""); 
                    } else {
                        setIsConfigured(false);
                        // TRANSLATION: Code not configured
                        setError(t('guard.no_code_configured', { dashboard: dashboardKey }));
                    }
                } else {
                    // TRANSLATION: User not found
                    setError(t('guard.user_not_found', { uid: user.uid }));
                    setIsConfigured(false);
                }
            } catch (e) {
                // TRANSLATION: DB Error
                setError(t('guard.load_error', { msg: e.message }));
                setIsConfigured(false);
            } finally {
                setIsLoading(false); 
            }
        };

        if (user && user.uid) {
            loadCodes();
        } else {
            setIsLoading(false); 
        }
        
    }, [user, normalizedKey, dashboardKey, hasPermanentAccess, getSessionAccessStatus, t]);
    
    const handleValidation = () => {
        setError(""); 
        const code = storedCodes[normalizedKey];

        if (!code) {
            setError(t('guard.internal_error'));
            return;
        }

        if (enteredCode.trim() === code.trim()) {
            setValidated(true);
            sessionStorage.setItem(SESSION_ACCESS_KEY_PREFIX + normalizedKey, 'true'); 
            setEnteredCode("");
        } else {
            setError(t('guard.invalid_code'));
        }
    };

    if (isLoading) {
        return <div className="container card" style={{ padding: '20px', textAlign: 'center' }}>{t('guard.loading')}</div>;
    }

    if (validated) {
        return children;
    }

    if (!isConfigured) {
        return (
             <div 
                 className="container card" 
                 style={{ marginTop: "80px", maxWidth: "500px", textAlign: "center", border: '2px solid red', padding: '20px', margin: 'auto' }}
             >
                 <h2>{t('guard.access_denied')} 🛑</h2>
                 <p style={{ color: 'red', fontWeight: 'bold' }}>{error}</p>
             </div>
        );
    }

    return (
        <div className="container card" style={{ marginTop: "80px", maxWidth: "500px", margin: 'auto', textAlign: "center", padding: '30px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}>
            <h2>{t('guard.title')}</h2>
            <p className="text-sm text-gray-600">
                {t('guard.subtitle', { dashboard: dashboardKey.toUpperCase() })}
            </p>

            {error && (
                <div style={{ backgroundColor: '#ffe3e3', color: '#d9534f', padding: '10px', borderRadius: '5px', marginBottom: '15px' }}>
                    {error}
                </div>
            )}

            <input
                type="password"
                placeholder={t('guard.placeholder')}
                value={enteredCode}
                onChange={(e) => setEnteredCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleValidation()}
                style={{ width: "100%", marginTop: "10px", padding: "12px", fontSize: '16px', border: '1px solid #ccc', borderRadius: '5px' }}
            />

            <button
                className="btn btn-primary"
                style={{ width: "100%", marginTop: "15px", padding: "12px", fontSize: '16px', cursor: 'pointer', background: 'var(--color-primary)', color: 'white', border: 'none', borderRadius: '5px', fontWeight: 'bold' }}
                onClick={handleValidation}
            >
                {t('guard.unlock_button')}
            </button>
        </div>
    );
}